"use client";

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { api } from "@/lib/axios";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

type Product = {
  id: number;
  name: string;
  selling_price: string | number;
  sku?: string;
};

export default function MarketingCataloguePage() {
  const { data: products } = useQuery({
    queryKey: ["products", "catalogue"],
    queryFn: async () => {
      const { data } = await api.get<{ data: Product[] }>("/api/products", {
        params: { per_page: 80, page: 1 },
      });
      return data.data;
    },
  });

  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [preview, setPreview] = useState("");

  const filtered = useMemo(() => {
    const list = products ?? [];
    const s = q.trim().toLowerCase();
    if (!s) {
      return list;
    }
    return list.filter(
      (p) =>
        p.name.toLowerCase().includes(s) ||
        String(p.sku ?? "").toLowerCase().includes(s),
    );
  }, [products, q]);

  const ordered = useMemo(() => {
    return selected
      .map((id) => (products ?? []).find((p) => p.id === id))
      .filter(Boolean) as Product[];
  }, [selected, products]);

  function toggle(id: number) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function move(ix: number, dir: -1 | 1) {
    const j = ix + dir;
    if (j < 0 || j >= selected.length) {
      return;
    }
    setSelected((prev) => {
      const n = [...prev];
      [n[ix], n[j]] = [n[j], n[ix]];

      return n;
    });
  }

  return (
    <div className="space-y-6 p-6">
      <Link
        to="/marketing"
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-2 inline-flex")}
      >
        <ArrowLeft className="h-4 w-4" />
        Marketing
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">Catalogue picker</h1>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Products</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input placeholder="Filter…" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="max-h-[420px] overflow-y-auto rounded-md border divide-y">
              {filtered.map((p) => (
                <label
                  key={p.id}
                  className="flex cursor-pointer items-center gap-2 p-2 text-sm hover:bg-muted/50"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(p.id)}
                    onChange={() => toggle(p.id)}
                  />
                  <span className="flex-1">{p.name}</span>
                  <span className="text-muted-foreground tabular-nums">
                    ₹{Number(p.selling_price).toFixed(0)}
                  </span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Arrange & preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="space-y-1">
              {ordered.map((p, ix) => (
                <li
                  key={p.id}
                  className="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-sm"
                >
                  <span className="flex-1 truncate">{p.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => move(ix, -1)}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => move(ix, 1)}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                const lines = ordered.map(
                  (p, i) => `${i + 1}. ${p.name} — ₹${Number(p.selling_price).toFixed(2)}`,
                );
                setPreview(
                  lines.length
                    ? `Hi! Featured today:\n${lines.join("\n")}\nReply STOP to opt out.`
                    : "",
                );
              }}
            >
              Build message draft
            </Button>
            <Textarea rows={8} value={preview} onChange={(e) => setPreview(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              Copy to WhatsApp campaign or connect WATI template manually.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
